import React, { useEffect } from 'react'
import { Link } from '@s4l/react-facade'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { buildLocation } from 'lib/utils/page'
import { fetchUserWalletRequestAction } from 'client/contexts/UserWallet/actions'
import { getAvailableCredit } from 'client/contexts/UserWallet/selectors'
import { getCurrencyFromUserMarket } from './utils'
import { getLoginData } from 'client/contexts/Login/selectors'
import { usePage } from 'src/hooks/usePage'
import { useMarket } from 'src/hooks/useMarket'
import { useSystem } from 'src/hooks/useSystem'
import { Button, DropdownSelector, Typography } from '@s4l/ds'
import { LoginButton } from '@s4l/uikit'

import { useS4LContext } from '@s4l/react-context'
import { IconTrophy } from '@s4l/icons/IconTrophy'
import { IconStaywallet } from '@s4l/icons/IconStaywallet'
import { IconPeople } from '@s4l/icons/IconPeople'
import { IconCallCenter } from '@s4l/icons/IconCallCenter'
import { IconEyeOpen } from '@s4l/icons/IconEyeOpen'
import { IconBed } from '@s4l/icons/IconBed'
import { IconSignout } from '@s4l/icons/IconSignout'
import { IconHeartVoid } from '@s4l/icons/IconHeartVoid'

import Price from 'client/components/Price'
import styles from './styles.module.css'
import Cookies from 'js-cookie'
import { COOKIE_GOOGLE_ACCESS_TOKEN } from 'src/constants/cookies'

interface SidebarMenuProps {
  readonly handleLogOut: (event: React.MouseEvent<HTMLButtonElement>) => void
  readonly handleClose?: (event: React.MouseEvent<HTMLAnchorElement>) => void
}

const SidebarMenu = ({ handleLogOut, handleClose }: SidebarMenuProps) => {
  const { pageId } = usePage()
  const { market } = useMarket()
  const { apiHostName } = useSystem()
  const { i18n } = useS4LContext()

  const dispatch = useDispatch()
  const user = useSelector(getLoginData, shallowEqual)
  const currencies = useSelector(getAvailableCredit)
  const currency = getCurrencyFromUserMarket(currencies, market)

  useEffect(() => {
    dispatch(fetchUserWalletRequestAction({ hostname: apiHostName }))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className={styles.avatarWrapper}>
        <LoginButton
          variant="text-on-all-devices"
          size="medium"
          disabled
          customGreetings={i18n.formatMessage(
            { id: user?.name ? 'global.hello-username' : 'global.hello-short' },
            { username: user?.name }
          )}
          userJSON={
            user?.userId
              ? {
                  id: user?.userId.toString(),
                  email: user?.email,
                  name: user?.name,
                  googleAccessToken: Cookies.get(COOKIE_GOOGLE_ACCESS_TOKEN),
                }
              : undefined
          }
        />
      </div>

      <DropdownSelector
        component={Link}
        key="bookings"
        {...buildLocation({ page: 'myBookings' })}
        leadingIcon={<IconBed />}
        onClick={handleClose}
        selected={pageId === 'myBookings'}
      >
        {i18n.formatMessage({ id: 'user.bookings' })}
      </DropdownSelector>
      <DropdownSelector
        component={Link}
        key="personalDetails"
        {...buildLocation({ page: 'myDetails' })}
        leadingIcon={<IconPeople />}
        onClick={handleClose}
        selected={pageId === 'myDetails'}
      >
        {i18n.formatMessage({ id: 'user.personal_details' })}
      </DropdownSelector>
      {user?.subscriptionPrime && (
        <DropdownSelector
          component={Link}
          key="myStayforlongPrime"
          {...buildLocation({ page: 'prime' })}
          leadingIcon={<IconTrophy />}
          onClick={handleClose}
          selected={pageId === 'prime'}
        >
          {i18n.formatMessage(
            {
              id: 'prime.my-stayforlong-title',
            },
            {
              prime: i18n.formatMessage({ id: 'global.prime' }),
              stayforlong: i18n.formatMessage({ id: 'global.stayforlong' }),
            }
          )}
        </DropdownSelector>
      )}
      {process.env.NEXT_PUBLIC_IS_LIKE_ENABLED === 'true' && (
        <DropdownSelector
          component={Link}
          key="propertiesLiked"
          {...buildLocation({ page: 'propertiesLiked' })}
          leadingIcon={<IconHeartVoid />}
          onClick={handleClose}
          selected={pageId === 'propertiesLiked'}
        >
          {i18n.formatMessage({ id: 'property-like.page-title' })}
        </DropdownSelector>
      )}
      <DropdownSelector
        component={Link}
        key="hotelsSeen"
        {...buildLocation({ page: 'hotelsSeen' })}
        leadingIcon={<IconEyeOpen />}
        onClick={handleClose}
        selected={pageId === 'hotelsSeen'}
      >
        {i18n.formatMessage({ id: 'global.hotels-seen' })}
      </DropdownSelector>
      <DropdownSelector
        component={Link}
        key="credit"
        {...buildLocation({ page: 'myStaywallet' })}
        leadingIcon={<IconStaywallet color="var(--color-primary-500)" />}
        onClick={handleClose}
        selected={pageId === 'myStaywallet'}
      >
        <span>{i18n.formatMessage({ id: 'staywallet.credit' })}</span>
        <Typography variant="body" size="l" bold>
          &nbsp;(
          {i18n.formatMessage(
            { id: 'global.wallet-available' },
            {
              price: (
                <Typography color="var(--color-success-500)" variant="body" size="l" bold>
                  +<Price amount={currency?.value || 0} currency={currency?.currency} />
                </Typography>
              ),
            }
          )}
          )
        </Typography>
      </DropdownSelector>
      <DropdownSelector
        component={Link}
        key="communicationPreferences"
        {...buildLocation({ page: 'myCommunicationPreferences' })}
        leadingIcon={<IconCallCenter />}
        onClick={handleClose}
        selected={pageId === 'myCommunicationPreferences'}
      >
        {i18n.formatMessage({ id: 'user.communication-preferences' })}
      </DropdownSelector>
      <Button variant="text" onClick={handleLogOut} fullWidth data-testid="logout-button">
        <Typography
          className="display-flex"
          variant="body"
          size="l"
          as="span"
          color="var(--color-error-500)"
          bold
        >
          <IconSignout />
          <span className="ml-8 align-self-center">
            {i18n.formatMessage({ id: 'user.logout' })}
          </span>
        </Typography>
      </Button>
    </div>
  )
}

export default SidebarMenu
